import {
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { TableCard } from "./TimeTracking.styles";
import { TimeTrackingEntry } from "../../../types/timeTracking";
import { EmployeeUserType } from "../../../types/employeeType";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import ServerSideTableColumnHeader from "../../../components/common/table/ServerSideTableColumnHeader";
import { PaginatedData } from "../../../hooks/PaginationHooks";

type Filters = {
  employeeUserId: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

type Props = {
  entries: TimeTrackingEntry[];
  isLoading: boolean;
  filters: Filters;
  employees: EmployeeUserType[];
  onFiltersChange: (filters: Partial<Filters>) => void;
  onClearFilters: () => void;
  getEmployeeName: (employeeId: string) => string;
  paginatedData: PaginatedData<TimeTrackingEntry>;
  hideEmployeeFilter?: boolean;
};

const formatDateTime = (value: string) =>
  dayjs(value).format("DD.MM.YYYY HH:mm");

const formatTime = (value: string) => dayjs(value).format("HH:mm");

const TimeTrackingCompanyTable = ({
  entries,
  isLoading,
  filters,
  employees,
  onFiltersChange,
  onClearFilters,
  getEmployeeName,
  paginatedData,
  hideEmployeeFilter = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const dateFormat = i18n.language === "de" ? "DD.MM.YYYY" : "MM/DD/YYYY";

  return (
    <TableCard>
      <Stack
        direction={{ xs: "column", md: "row" }}
        sx={{ justifyContent: "space-between", gap: 2 }}
      >
        <Typography variant="h6">
          {t("serviceModulesPage.timetrackingPage.companyTable.title")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {!hideEmployeeFilter && (
            <TextField
              select
              label={t("serviceModulesPage.timetrackingPage.companyTable.employeeFilter")}
              value={filters.employeeUserId}
              onChange={(event) =>
                onFiltersChange({ employeeUserId: event.target.value })
              }
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">
                {t("serviceModulesPage.timetrackingPage.companyTable.employeeAll")}
              </MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {`${employee.firstName} ${employee.lastName}`}
                </MenuItem>
              ))}
            </TextField>
          )}
          <DatePicker
            label={t("serviceModulesPage.timetrackingPage.companyTable.startFilter")}
            value={filters.startDate}
            onChange={(value) => onFiltersChange({ startDate: value })}
            format={dateFormat}
          />
          <DatePicker
            label={t("serviceModulesPage.timetrackingPage.companyTable.endFilter")}
            value={filters.endDate}
            onChange={(value) => onFiltersChange({ endDate: value })}
            minDate={filters.startDate ?? undefined}
            format={dateFormat}
          />
          <GeneralButton appVariant="ghost" onClick={onClearFilters}>
            {t("serviceModulesPage.timetrackingPage.companyTable.clearFilters")}
          </GeneralButton>
        </Stack>
      </Stack>

      {isLoading ? (
        <CircularProgress />
      ) : entries.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {t("serviceModulesPage.timetrackingPage.companyTable.empty")}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <ServerSideTableColumnHeader
                  label={t("serviceModulesPage.timetrackingPage.table.employee")}
                  fieldName="employeeUserId"
                  isSorted
                  paginatedData={paginatedData}
                />
              </TableCell>

              <TableCell>
                <ServerSideTableColumnHeader
                  label={t("serviceModulesPage.timetrackingPage.table.start")}
                  fieldName="startTimestamp"
                  isSorted
                  paginatedData={paginatedData}
                />
              </TableCell>

              <TableCell>
                <ServerSideTableColumnHeader
                  label={t("serviceModulesPage.timetrackingPage.table.end")}
                  fieldName="endTimestamp"
                  isSorted
                  paginatedData={paginatedData}
                />
              </TableCell>

              <TableCell align="right">
                <ServerSideTableColumnHeader
                  label={t("serviceModulesPage.timetrackingPage.table.netWorkingHours")}
                  fieldName="netWorkingHours"
                  isSorted
                  paginatedData={paginatedData}
                />
              </TableCell>

              <TableCell>
                {t("serviceModulesPage.timetrackingPage.table.pauseCount")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{getEmployeeName(entry.employeeUserId)}</TableCell>
                <TableCell>{formatDateTime(entry.startTimestamp)}</TableCell>
                <TableCell>{formatDateTime(entry.endTimestamp)}</TableCell>
                <TableCell align="right">{entry.netWorkingHours}</TableCell>
                <TableCell>
                  {entry.pauses.length === 0 ? (
                    "-"
                  ) : (
                    <Stack spacing={0.5}>
                      {entry.pauses.map((pause) => (
                        <Typography
                          key={`${pause.startTimestamp}-${pause.endTimestamp}`}
                          variant="body2"
                        >
                          {`${formatTime(pause.startTimestamp)} - ${formatTime(pause.endTimestamp)}`}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableCard>
  );
};

export default TimeTrackingCompanyTable;
